import { PermissionGate } from "@components/PermissionGate";
import { useCallback, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import {
  Button,
  Modal,
  NumberInput,
  ScrollAreaAutosize,
  Select,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { IconFileUpload,IconInfoCircle, IconUpload } from "@tabler/icons-react";
import i18n from "src/i18n";
import { z } from "zod";

import { getLabCategoryValues } from "@data/dropdownValues";

import { useCreateClinicLaboratoryService } from "@hooks/mutations/clinic/useCreateClinicLaboratoryService";
import { useGetClinicCollectionMethodsOptions } from "@hooks/queries/clinic/useGetClinicCollectionMethodsOptions";
import { useGetPartners } from "@hooks/queries/clinic/useGetPartners";

import { StyledAddLaboratoryServiceModalContent } from "./AddLaboratoryServiceModal.styled";


const singleFormSchema = () =>
  z.object({
    type: z.string().min(1, i18n.t("name-required")),
    category: z.string().optional(),
    price: z.number().min(1, i18n.t("price-required")),
    collectionMethod: z.string().optional(),
    collectionPrice: z.number().min(0).optional(),
    partner_id: z.string().optional(),
  });

type SingleFormInputs = z.infer<ReturnType<typeof singleFormSchema>>;

interface AddLaboratoryServiceModalProps {
  opened: boolean;
  close: () => void;
}



export const AddAnalysisServiceModal = ({
  opened,
  close,
}: AddLaboratoryServiceModalProps) => {
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState<string | null>("single");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const {
    mutate: createLaboratoryService,
    isLoading: isCreateLaboratoryServiceLoading,
  } = useCreateClinicLaboratoryService();

  const {
    data: collectionMethodsData,
    isLoading: isCollectionMethodsLoading,
  } = useGetClinicCollectionMethodsOptions();

  const { data: partnersData } = useGetPartners({ isActive: true });

  const partnersOptions = useMemo(() => {
    return (partnersData || []).map(partner => ({
      label: partner.name,
      value: partner._id,
    }));
  }, [partnersData]);

  const singleForm = useForm({
    mode: "uncontrolled",
    validate: zodResolver(singleFormSchema()),
    initialValues: {
      type: "",
      category: "",
      price: 0,
      collectionMethod: "",
      collectionPrice: 0,
      partner_id: "",
    },
  });

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([...uploadedFiles, ...acceptedFiles]);
    },
    [uploadedFiles]
  );

  const {
    getRootProps,
    getInputProps,
    isDragAccept,
    isDragActive,
    isDragReject,
  } = useDropzone({
    accept: {
      "text/csv": [".csv"],
    },
    onDrop,
    multiple: false,
    maxFiles: 1,
  });

  const removeFile = (file: File) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(newFiles.indexOf(file), 1);
    setUploadedFiles(newFiles);
  };

  const handleSingleFormSubmit = (values: SingleFormInputs) => {
    // Convert collection method ID to name
    let collectionMethodName = values.collectionMethod;
    if (values.collectionMethod && collectionMethodsData) {
      const selectedMethod = collectionMethodsData.find(
        method => method.value === values.collectionMethod
      );
      collectionMethodName = selectedMethod?.label || values.collectionMethod;
    }

    createLaboratoryService(
      {
        ...values,
        collectionMethod: collectionMethodName,
      },
      {
        onSuccess: () => {
          singleForm.reset();
          close();
        },
      }
    );
  };

  const handleBatchSubmit = () => {
    // TODO: Implement batch upload functionality
    console.log("Batch upload files:", uploadedFiles);
    close();
  };



  const handleClose = () => {
    singleForm.reset();
    setUploadedFiles([]);
    setCurrentTab("single");
    close();
  };

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("add-laboratory-type")}</Text>}
      opened={opened}
      onClose={handleClose}
      scrollAreaComponent={ScrollAreaAutosize}
      size="md">
      <StyledAddLaboratoryServiceModalContent>
        <Tabs value={currentTab} onChange={setCurrentTab}>
          <Tabs.List grow>
            <Tabs.Tab value="single">{t("single")}</Tabs.Tab>
            <Tabs.Tab value="batch">{t("batch")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="single">
            <form
              className="form"
              onSubmit={singleForm.onSubmit(
                values => handleSingleFormSubmit(values),
                errors => {
                  const firstErrorPath = Object.keys(errors)[0];
                  singleForm.getInputNode(firstErrorPath)?.focus();
                }
              )}>
              <div className="form-field">
                <TextInput
                  label={t("type")}
                  withAsterisk
                  placeholder={t("name")}
                  {...singleForm.getInputProps("type")}
                  key={singleForm.key("type")}
                />
              </div>

              <div className="form-field">
                <NumberInput
                  label={t("price")}
                  withAsterisk
                  placeholder="30000"
                  {...singleForm.getInputProps("price")}
                  key={singleForm.key("price")}
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("collection-method")}
                  placeholder={t("select-a-collection-method")}
                  data={[
                    { label: "-", value: "" },
                    ...(collectionMethodsData || [])
                  ]}
                  {...singleForm.getInputProps("collectionMethod")}
                  disabled={isCollectionMethodsLoading}
                />
              </div>

              <div className="form-field">
                <NumberInput
                  label={t("collection-price")}
                  placeholder="500"
                  {...singleForm.getInputProps("collectionPrice")}
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("partner")}
                  placeholder={t("select-partner")}
                  data={partnersOptions}
                  searchable
                  clearable
                  {...singleForm.getInputProps("partner_id")}
                />
              </div>

              <div className="form-field">
                <Select
                  label={t("category")}
                  placeholder={t("select-category")}
                  data={getLabCategoryValues()}
                  {...singleForm.getInputProps("category")}
                />
              </div>

              <div className="form-actions">
                <PermissionGate permission="resources.create"><Button
                  type="submit"
                  loading={isCreateLaboratoryServiceLoading}
                  disabled={isCreateLaboratoryServiceLoading}>
                  {t("submit")}
                </Button></PermissionGate>
              </div>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="batch">
            <div className="batch-content">
              <div className="info-section">
                <div className="info-icon">
                  <IconInfoCircle size={20} />
                </div>
                <p className="info-text">
                  {t("upload-csv-labs-info")}
                </p>
              </div>

              <div className="files-wrapper">
                <div
                  {...getRootProps({
                    className: `dropzone ${isDragActive ? "active" : ""} ${isDragAccept ? "accept" : ""
                      } ${isDragReject ? "reject" : ""}`,
                  })}>
                  <input {...getInputProps()} />
                  <div className="text-content">
                    <div className="svg-wrapper">
                      <IconFileUpload size={28} />
                    </div>
                    <div className="p-wrapper">
                      <p>{t("drag-file-here")}</p>
                      <p>{t("or-click-to-browse")}</p>
                    </div>
                  </div>
                </div>

                {uploadedFiles.length > 0 && (
                  <div className="uploaded-files">
                    <h4>{t("uploaded-files")}</h4>
                    <ul>
                      {uploadedFiles.map((file, index) => (
                        <li key={index}>
                          <div className="content">
                            <span>{file.name}</span>
                          </div>
                          <Button
                            variant="subtle"
                            size="xs"
                            color="red"
                            onClick={() => removeFile(file)}>
                            {t("remove")}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <Button
                  mt={"lg"}
                  onClick={handleBatchSubmit}
                  disabled={uploadedFiles.length === 0}>
                  {t("submit")}
                </Button>
              </div>
            </div>
          </Tabs.Panel>
        </Tabs>
      </StyledAddLaboratoryServiceModalContent>
    </Modal>
  );
};
